import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { useHistory } from 'react-router';
import Registration from '../../components/Registration/Registration';
import { ReactComponent as PhoneLogo } from '../../assets/images/svg/phone.svg';
import Logo from '../../components/Logo/Logo';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import {
  mediaBelow,
  mediaBetween,
  mediaUp,
  pxToRem,
} from '../../assets/styles/helper';
import config from '../../config';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import LoaderIcon from '../../components/Loader/LoaderIcon';
import LoaderContainer from '../../components/Loader/LoaderContainer';
import LoaderWrap from '../../components/Loader/LoaderWrap';
// import { EMAIL_LOGIN_PAGE } from '../../constants/pages';
import { useBrokerName } from '../../util/hooks/userBrokerName';
import RegistrationPageOne from '../../components/Registration/RegistrationPageOne';
import Paragraph from '../../components/Paragraph/Paragraph';
import Anchor from '../../components/Anchor/Anchor';
import RegistrationPageTwo from '../../components/Registration/RegistrationPageTwo';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import { ReactComponent as ChevronLeft } from '../../assets/images/svg/chevron-left.svg';
import RegistrationSidebarWithPrompt from './RegistrationSidebarWithPrompt';
import InsiderPlusRegistrationSidebar from './InsiderPlusRegistrationSidebar';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { INSIDER_PLUS_ENTRY_SOURCE } from '../../constants/sessionStorage';
import {
  DISCOURSE_SOURCE,
  OUTER_PORTAL_SOURCE,
  pushInsiderFunnelEventOnce,
} from '../../util/helpers/insiderPlusEntryHelper';

const getSidebarBackground = () => {
  const { brokerTheme } = config;

  switch (brokerTheme) {
    case 'GoldSilver':
    case 'Contoso':
    case 'LegacyPM':
    default:
  }
};
const RegistrationBody = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '0.5fr 1fr')};
  min-height: ${({ isMobile, footerHeight }) =>
    isMobile
      ? 'auto'
      : `calc(100vh - ${footerHeight}px - calc(2rem * var(--legacy-scale, 1)))`};
  overflow: visible;
  margin: ${({ isMobile }) =>
    isMobile ? '' : 'calc(1rem * var(--legacy-scale, 1))'};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    ${({ isGS }) =>
      isGS ? `` : `margin-bottom: calc(-4rem * var(--legacy-scale, 1));`};
  }
  ${mediaBelow(variables.breakpoints.bpLg)} {
    ${({ isGS }) => (isGS ? `` : `display: flex; flex-direction: column;`)};
  }
  ${mediaBetween(variables.breakpoints.bpLg, variables.breakpoints.bp1366)} {
    grid-template-columns: 40% 60%;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bpXxxl)} {
    grid-template-columns: 534px auto;
  }
  ${mediaBetween(variables.breakpoints.bpXxxl, variables.breakpoints.bp1600)} {
    grid-template-columns: 612px auto;
  }
  ${mediaBetween(variables.breakpoints.bp1600, variables.breakpoints.bp1920)} {
    grid-template-columns: 750px auto;
  }
  ${mediaUp(variables.breakpoints.bp1920)} {
    grid-template-columns: 850px auto;
  }
`;

const BannerWrapper = styled.div`
  display: flex;
  background: ${themeColors.colorPrimary};
  width: 100%;
  padding: ${variables.margins.fourteen};
`;

const RegistrationSidebar = styled.div`
  ${({ isGS }) =>
    isGS
      ? `background: ${themeColors.colorSidebarBegin};`
      : `background-image: url(${getSidebarBackground()});
    `};
  background-size: ${({ isGS }) => (isGS ? 'contain' : 'cover')};
  ${({ isGS }) =>
    isGS && `background-repeat: no-repeat; background-position: right bottom`};
  padding: calc(1rem * var(--legacy-scale, 1));
  border-radius: 8px;
  margin-bottom: calc(1rem * var(--legacy-scale, 1));

  svg {
    color: ${themeColors.colorHeaderLogoLight};
    height: calc(1.75rem * var(--legacy-scale, 1));
    object-fit: contain;
  }
`;

const RegistrationStepWrapper = styled.div`
  padding: 16px;
  width: 100%;
  max-width: 600px;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    margin: 0;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bp1600)} {
    max-width: 636px;
  }
  ${mediaUp(variables.breakpoints.bp1600)} {
    max-width: 650px;
  }
  ${mediaBetween(variables.breakpoints.bpLg, variables.breakpoints.bpXl)} {
    width: auto;
  }
`;

const RegistrationContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${mediaBelow(variables.breakpoints.bpXxxl)} {
    align-items: flex-start;
  }
`;

const RegistrationTitle = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: normal;
  color: ${themeColors.colorTextPrimary};
  margin-top: 0px;
  text-align: left;
  font-family: ${variables.family.goldSilver};
  ${mediaBelow(variables.breakpoints.bpXxxl)} {
    font-size: 24px;
  }
  margin-bottom: 16px;
  ${mediaBelow(variables.breakpoints.bpLg)} {
    line-height: 26px;
    margin-bottom: 10px;
  }
`;

const RegistrationSubtitle = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 16px;
  line-height: 1.6;
  color: ${themeColors.colorText};
  font-weight: 600;
  margin-bottom: 0;
  line-break: loose;

  ${mediaBelow(variables.breakpoints.bpXs)} {
    font-size: 14px;
  }
`;

const PhoneIcon = styled(PhoneLogo)`
  width: 24px;
  height: 24px;
  margin-right: ${variables.margins.eight};
  flex-shrink: 0;
`;

const PhoneWrapper = styled.span`
  font-weight: bold;
  white-space: nowrap;
`;

const BackNavigatorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-left: ${({ marginLeft }) => (marginLeft ? pxToRem(marginLeft) : '')};
  margin-top: ${({ marginTop }) => (marginTop ? pxToRem(marginTop) : '')};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? pxToRem(marginBottom) : pxToRem(30)};
  margin-right: ${({ marginRight }) =>
    marginRight ? pxToRem(marginRight) : ''};
  ${mediaBelow(variables.breakpoints.bpLg)} {
    padding: 16px;
    margin: 0;
  }
`;

const ChevronIconWrapper = styled.div`
  display: flex;
  margin-right: ${pxToRem(10)};
  cursor: pointer;
`;

const StepHeader = styled.div`
  display: flex;
  padding: 16px 0;
`;

const MAX_STEPS = 2;
const FIRST_STEP = 1;
const STEP_INCREMENT = 1;

const TwoStepQuickRegistration = () => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const [registrationUrl, setRegistrationUrl] = useState('');
  const history = useHistory();
  // Capture whether the registration was opened in a new tab (no prior history)
  const [openedInNewTab] = useState(() => window.history.length === 1);
  const brokerName = useBrokerName();
  const [stepNumber, setStepNumber] = useState(FIRST_STEP);
  // const [showConfirmation, setShowConfirmation] = useState(false);
  const windowSize = useWindowSize();
  const isMobile =
    (windowSize.width ?? window.innerWidth) < variables.breakpoints.bpLg;
  const [isOpen, setIsOpen] = useState(false);
  const [supportPhoneNumber, setSupportPhoneNumber] = useState('');
  const [footerHeight, setFooterHeight] = useState(0);
  // [Insider+] Registration variant — swaps copy, sidebar, and support-phone
  // text when the user arrives via the outer portal or Discourse SSO.
  const insiderPlusEntrySource = retrieveFromSessionStorage(
    INSIDER_PLUS_ENTRY_SOURCE,
  );
  const isInsiderPlusVariant =
    insiderPlusEntrySource === OUTER_PORTAL_SOURCE ||
    insiderPlusEntrySource === DISCOURSE_SOURCE;

  // Funnel: registration entered. Once per journey — the component can remount
  // (e.g. leave to Login and navigate back) within the same Insider+ journey.
  useEffect(() => {
    pushInsiderFunnelEventOnce('registration_start');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    removeFromSessionStorage('twostep1');
    removeFromSessionStorage('twostep2');

    const handleBeforeUnload = () => {
      removeFromSessionStorage('twostep1');
      removeFromSessionStorage('twostep2');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeFromSessionStorage('twostep1');
      removeFromSessionStorage('twostep2');
    };
  }, []);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return;
    footer.style.display = isOpen ? 'none' : '';
  }, [isOpen]);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(entry.target.getBoundingClientRect().height);
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const footer = document.getElementById('app-footer');
    if (!footer) return undefined;
    if (!isMobile) {
      footer.style.position = 'fixed';
      footer.style.bottom = '0px';
    } else {
      footer.style.position = 'static';
      footer.style.bottom = 'auto';
    }
    return () => {
      footer.style.position = '';
      footer.style.bottom = '';
    };
  }, [isMobile]);

  useEffect(() => {
    if (!settings?.SupportPhone) {
      return;
    }

    setSupportPhoneNumber(settings.SupportPhone);
  }, [settings.SupportPhone]);

  const BackNavigator = () => (
    <>
      <BackNavigatorWrapper
        marginLeft={16}
        marginTop={8}
        marginBottom={8}
        onClick={() => setIsOpen(true)}
      >
        <ChevronIconWrapper>
          <ChevronLeft
            height={24}
            marginLeft="50px"
            stroke={themeColors.colorNavIcon}
          />
        </ChevronIconWrapper>
        <Paragraph bold fontSize={16} usePx>
          {t('quickRegistration.twoStep.rightPane.createAccount')}
        </Paragraph>
      </BackNavigatorWrapper>
    </>
  );

  // Ref so the popstate handler always reads the latest stepNumber without stale closure
  const stepNumberRef = useRef(stepNumber);
  useEffect(() => {
    stepNumberRef.current = stepNumber;
  });

  // On mobile: push a native guard entry before first paint so browser back is
  // always interceptable, then listen for popstate to show USP or return to step 1.
  useLayoutEffect(() => {
    if (!isMobile) return undefined;
    window.history.pushState({ registrationGuard: true }, '');
    const handlePopState = () => {
      if (stepNumberRef.current === MAX_STEPS) {
        setStepNumber(FIRST_STEP);
      } else {
        setIsOpen(true);
      }
      // Re-push guard so every subsequent back press is also intercepted
      window.history.pushState({ registrationGuard: true }, '');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile]);

  const updateStep = (currentStep) => {
    if (
      typeof currentStep !== 'number' ||
      currentStep + STEP_INCREMENT > MAX_STEPS
    ) {
      return FIRST_STEP;
    }
    if (currentStep === FIRST_STEP) {
      // Once per journey — the user can go back to step 1 and resubmit.
      pushInsiderFunnelEventOnce('registration_step_complete', {
        step: FIRST_STEP,
      });
    }
    setStepNumber(currentStep + 1);
    window.scrollTo(0, 0);
    // Push a history entry so desktop browser back can return to step 1
    history.push(history.location.pathname + history.location.search);
  };

  const backStep = (currentStep) => {
    if (typeof currentStep !== 'number') return;
    if (currentStep > FIRST_STEP) {
      setStepNumber(currentStep - STEP_INCREMENT);
      window.scrollTo(0, 0);
      return;
    }
    // Step 1 back (chevron click)
    if (isMobile) {
      setIsOpen(true); // Show USP screen
    } else if (!openedInNewTab) {
      history.goBack(); // Desktop same tab → previous page
    }
    // Desktop new tab: chevron is hidden — this branch is never reached
  };

  // Desktop only: intercept browser back on step 2 to return to step 1
  useEffect(() => {
    const unlisten = history.listen((_location, action) => {
      if (action !== 'POP') return;
      if (!isMobile && stepNumber === MAX_STEPS) {
        setStepNumber(FIRST_STEP);
      }
    });
    return unlisten;
  }, [history, stepNumber, isMobile]);

  useEffect(() => {
    if (!settings || isEmpty(settings)) {
      return;
    }
    const { BrokerCode, IsQuickRegistrationActive } = settings;
    if (BrokerCode && !IsQuickRegistrationActive) {
      const regUrl = settings?.RegistrationUrlV2;
      setRegistrationUrl(regUrl);
    }
  }, [settings]);

  // const goStepBack = () => {
  //   history.push(EMAIL_LOGIN_PAGE);
  // };

  if (registrationUrl && registrationUrl.length > 0) {
    window.location.replace(registrationUrl);
  }

  const LeftPane = () => {
    switch (brokerName) {
      case 'GoldSilver':
        if (isMobile) {
          if (isOpen) {
            return isInsiderPlusVariant ? (
              <InsiderPlusRegistrationSidebar
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            ) : (
              <RegistrationSidebarWithPrompt
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              >
                <Logo />
              </RegistrationSidebarWithPrompt>
            );
          }
          return <></>;
        }
        return isInsiderPlusVariant ? (
          <InsiderPlusRegistrationSidebar
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        ) : (
          <RegistrationSidebarWithPrompt isOpen={isOpen} setIsOpen={setIsOpen}>
            <Logo />
          </RegistrationSidebarWithPrompt>
        );

      case 'GBI Direct':
        return (
          <RegistrationSidebar isGS={config?.brokerTheme === 'GoldSilver'}>
            <Logo />
          </RegistrationSidebar>
        );
      default:
        return <></>;
    }
  };

  const Banner = () => (
    <BannerWrapper>
      <Logo />
    </BannerWrapper>
  );

  const renderQuickRegistrationPage = () => {
    if (brokerName === 'GoldSilver') {
      if (stepNumber === FIRST_STEP) {
        return (
          <RegistrationPageOne
            stepNumber={stepNumber}
            updateStep={updateStep}
            isOpen={isOpen}
          />
        );
      }
      return (
        <RegistrationPageTwo stepNumber={stepNumber} updateStep={updateStep} />
      );
    }
    return <Registration />;
  };

  if (
    !settings ||
    isEmpty(settings) ||
    settings?.BrokerName === undefined ||
    settings?.IsQuickRegistrationActive === undefined
  ) {
    return (
      <LoaderContainer>
        <LoaderWrap data-cy="loading-indicator">
          <LoaderIcon />
        </LoaderWrap>
      </LoaderContainer>
    );
  }

  if (isOpen && isMobile) {
    return (
      <RegistrationBody
        isMobile={isMobile}
        isGS={brokerName === 'GoldSilver'}
        footerHeight={footerHeight}
      >
        {isInsiderPlusVariant ? (
          <InsiderPlusRegistrationSidebar
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        ) : (
          <RegistrationSidebarWithPrompt isOpen={isOpen} setIsOpen={setIsOpen}>
            <Logo />
          </RegistrationSidebarWithPrompt>
        )}
      </RegistrationBody>
    );
  }

  return (
    <>
      {brokerName === 'GoldSilver' && isMobile && <BackNavigator />}
      {brokerName === 'GoldSilver' && isMobile && <Banner />}
      <RegistrationBody
        isMobile={isMobile}
        isGS={brokerName === 'GoldSilver'}
        footerHeight={footerHeight}
      >
        <LeftPane />
        <RegistrationContent>
          <RegistrationStepWrapper footerHeight={footerHeight}>
            <RegistrationTitle>
              {isInsiderPlusVariant
                ? t('insiderPlus.registration.title')
                : t('quickRegistration.twoStep.rightPane.title')}
            </RegistrationTitle>
            <RegistrationSubtitle>
              <PhoneIcon />
              {isInsiderPlusVariant ? (
                <>
                  {t('insiderPlus.registration.supportPrompt')}
                  <PhoneWrapper>
                    &nbsp;
                    <Anchor
                      type="telephone"
                      value={t('insiderPlus.registration.supportPhone')}
                      text={t('insiderPlus.registration.supportPhone')}
                      bold
                    />
                  </PhoneWrapper>
                </>
              ) : (
                <>
                  {t('quickRegistration.twoStep.rightPane.subtitle', {
                    brokerName,
                  })}
                  <PhoneWrapper>
                    &nbsp;
                    <Anchor
                      type="telephone"
                      value={settings?.SupportPhoneTollFree}
                      text={supportPhoneNumber}
                      bold
                    />
                  </PhoneWrapper>
                </>
              )}
            </RegistrationSubtitle>
            <StepHeader>
              {stepNumber === MAX_STEPS && (
                <ChevronIconWrapper onClick={() => backStep(stepNumber)}>
                  <ChevronLeft height={24} stroke={themeColors.colorPrimary} />
                </ChevronIconWrapper>
              )}
              <Paragraph
                bold
                color={themeColors.colorPrimary}
                fontSize={16}
                usePx
              >
                {t('quickRegistration.twoStep.rightPane.stepNumber', {
                  stepNumber,
                })}
              </Paragraph>
            </StepHeader>
            {renderQuickRegistrationPage()}
          </RegistrationStepWrapper>
        </RegistrationContent>
      </RegistrationBody>
    </>
  );
};

TwoStepQuickRegistration.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};
export default TwoStepQuickRegistration;
